import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  cancelProposedChip,
  proposeAvailableChip,
} from "core-integration/src/store/chips/actions";
import {
  getAvailableChipNames,
  getProposedChipName,
} from "core-integration/src/store/chips/reducers";
import { getElementsById } from "core-integration/src/store/elements/reducers";
import { IElementsById } from "core-integration/src/store/elements/types";
import { getNextEvent } from "core-integration/src/store/events/reducers";
import { IEvent } from "core-integration/src/store/events/types";
import { ITransferState } from "core-integration/src/store/my-team/types";
import {
  getFreeTransfers,
  getProposedTransfers,
  getTransferCosts,
  getTransferState,
} from "core-integration/src/store/squad/reducers";
import { makeTransfers as makeTransfersThunk } from "core-integration/src/store/squad/thunks";
import { ITransfersData } from "core-integration/src/store/squad/types";
import { formatRawAsLocalI18n } from "core-integration/src/utils/datetime";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { dateLocales } from "../../i18n";
import Alert, { AlertGroup, AlertItem } from "../Alert";
import Box from "../Box";
import Button from "../Button";
import Dialog, { DialogButtonItem } from "../Dialog";
import DialogHeading from "../DialogHeading";
import Table from "../Table";

interface IOwnProps {
  allowNavigation: () => void;
  handleHide: () => void;
}

const ConfirmTransfersDialog = ({ allowNavigation, handleHide }: IOwnProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<ThunkDispatch>();

  const elementsById: IElementsById = useSelector(getElementsById);
  const freeTransfers = useSelector(getFreeTransfers);
  const nextEvent: IEvent | null = useSelector(getNextEvent);
  const proposedChip = useSelector(getProposedChipName);
  const transferCosts = useSelector(getTransferCosts);
  const transfers: ITransfersData[] = useSelector(getProposedTransfers);
  const transferState: ITransferState | null = useSelector(getTransferState);
  const wildcardAvailable = useSelector(
    (state: RootState) => getAvailableChipNames(state).indexOf("wildcard") > -1,
  );

  const { i18n, t } = useTranslation();
  if (!nextEvent || !transferState) {
    return null;
  }

  const makeTransfers = () => dispatch(makeTransfersThunk());
  const cancelChip = (chip: string) => dispatch(cancelProposedChip(chip));
  const proposeChip = (chip: string) => dispatch(proposeAvailableChip(chip));

  const submitTransfers = async () => {
    allowNavigation();
    await makeTransfers();
    navigate("/my-team");
  };

  return (
    <Dialog closeDialog={handleHide}>
      <Dialog.Header closeDialog={handleHide}>
        <DialogHeading>
          {t("confirmTransfers.heading", "Confirm Transfers")}
        </DialogHeading>
      </Dialog.Header>
      <Dialog.Body isPadded={true}>
        <Box mb={4}>
          <Table>
            <thead>
              <tr>
                <th>{t("confirmTransfers.out", "Out")}</th>
                <th>{t("confirmTransfers.in", "In")}</th>
                <th>{t("confirmTransfers.cost", "Cost")}</th>
              </tr>
            </thead>
            <tbody>
              {transfers.map((t, index) => (
                <tr key={t.element_out}>
                  <td>
                    {t.element_out ? elementsById[t.element_out].web_name : "-"}
                  </td>
                  <td>{elementsById[t.element_in].web_name}</td>
                  <td>
                    {transferCosts && index >= freeTransfers
                      ? transferState.cost
                      : 0}{" "}
                    pts
                  </td>
                </tr>
              ))}
              <tr>
                <td />
                <td>{t("confirmTransfers.totalCost", "Total cost")}</td>
                <td>{transferCosts} pts</td>
              </tr>
            </tbody>
          </Table>
        </Box>
        <span className="notranslate">
          {transferCosts > 0 ? (
            <Alert type="warning">
              <AlertGroup>
                <AlertItem>
                  {t(
                    "confirmTransfers.transferAlert",
                    "These transfers will be active for {{name}} ({{time}}) and will deduct {{costs}}pts from your score!",
                    {
                      name: nextEvent.name,
                      time: formatRawAsLocalI18n(
                        nextEvent.deadline_time,
                        dateLocales[i18n.language],
                      ),
                      costs: transferCosts,
                    },
                  )}
                </AlertItem>
              </AlertGroup>
              {wildcardAvailable && proposedChip !== "uteam" && (
                <AlertGroup>
                  <AlertItem>
                    {t(
                      "confirmTransfers.playYourWildcard",
                      "Play your wildcard to make unlimited free transfers this Round.",
                    )}
                  </AlertItem>
                  <Box mt={4}>
                    <Button onClick={() => proposeChip("wildcard")} width={1}>
                      {t(
                        "confirmTransfers.playWildcardButton",
                        "Play Wildcard",
                      )}
                    </Button>
                  </Box>
                </AlertGroup>
              )}
            </Alert>
          ) : (
            <Alert type="warning">
              <AlertGroup>
                <AlertItem>
                  {t(
                    "confirmTransfers.theseTransfersWill",
                    "These transfers will be active for {{name}} ({{time}})",
                    {
                      name: nextEvent.name,
                      time: formatRawAsLocalI18n(
                        nextEvent.deadline_time,
                        dateLocales[i18n.language],
                      ),
                    },
                  )}
                </AlertItem>
              </AlertGroup>
              {proposedChip === "wildcard" && (
                <AlertGroup>
                  <AlertItem>
                    <strong>
                      {t(
                        "confirmTransfers.youAreProposingWildcard",
                        "You are proposing to play your wildcard!",
                      )}
                    </strong>
                  </AlertItem>
                  <AlertItem>
                    {t(
                      "confirmTransfers.cancelYourWildcard",
                      "Cancel your wildcard if you would prefer to spend points on these transfers.",
                    )}
                  </AlertItem>
                  <Button onClick={() => cancelChip("wildcard")} width={1}>
                    {t("confirmTransfers.cancelWildcard", "Cancel Wildcard")}
                  </Button>
                </AlertGroup>
              )}
              {proposedChip === "uteam" && (
                <AlertGroup>
                  <AlertItem>
                    <b>
                      {t(
                        "confirmTransfers.proposingUnlimit",
                        "You are proposing to play your Loan Rangers chip! This allows you to make unlimited free transfers and choose how many players you want from a specific team for a single round. At the next deadline, your squad is returned to how it was at the start of the round.",
                      )}
                    </b>
                  </AlertItem>
                  <AlertItem>
                    {t(
                      "confirmTransfers.cancelYourUnlimit",
                      "Cancel Loan Rangers chip if you would prefer not to do this.",
                    )}
                  </AlertItem>
                  <Button
                    onClick={() => {
                      cancelChip("uteam");
                      return handleHide();
                    }}
                    width={1}
                  >
                    {t("confirmTransfers.cancelUnlimit", "Cancel Loan Rangers")}
                  </Button>
                </AlertGroup>
              )}
            </Alert>
          )}
          <Box mt={4}>
            <div>
              <DialogButtonItem>
                <Button onClick={submitTransfers} width={1} variant="primary">
                  {t("confirmTransfers.confirmTransfers", "Confirm Transfers")}

                  {transferCosts > 0 && !proposedChip && (
                    <>
                      <br />
                      (-{transferCosts}pts)
                    </>
                  )}
                  {proposedChip === "wildcard" && (
                    <>
                      <br />(
                      {t(
                        "confirmTransfers.andPlayWildcard",
                        "and play Wildcard",
                      )}
                      )
                    </>
                  )}
                  {proposedChip === "uteam" && (
                    <>
                      <br />(
                      {t(
                        "confirmTransfers.andPlayUnlimit",
                        "and play Loan Rangers",
                      )}
                      )
                    </>
                  )}
                </Button>
              </DialogButtonItem>
              <DialogButtonItem>
                <Button variant="primary" onClick={handleHide} width={1}>
                  {t("confirmTransfers.changeTransfers", "Change Transfers")}
                </Button>
              </DialogButtonItem>
            </div>
          </Box>
        </span>
      </Dialog.Body>
    </Dialog>
  );
};

export default ConfirmTransfersDialog;
